import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { GridCloseIcon } from '@mui/x-data-grid';
import { filter, findIndex, last, lowerCase, range, upperFirst } from 'lodash';
import { Fragment, useMemo } from 'react';

import { OtaPlanDetails } from '@/api/ota/types';
import { useSpotterContext } from '@/context/SpotterContext';
import { useBmNodeVersionUpdates } from '@/hooks/useBmNodeVersionUpdates';
import { Dismissable, useDismissable } from '@/hooks/useDismissable';
import { blue, cardBgLightGrey, minorTextGrey } from '@/theme';
import { presentParticipleFirst } from '@/utils/languageHelpers';

/**
 * Shared by the in-progress and completed OTA progress bar implementations in this file, to
 * expand sub-steps (e.g. individual BM nodes) and map to user-facing descriptions. Also returns
 * a new currentStep scaled across the new step sequence.
 *
 * Each step's description should start with an action verb, so it can be passed to presentParticipleFirst
 *
 * If you're tempted to export this for use anywhere else, seriously consider pushing this
 * back into wavefleet's OTA model.
 */
const useExpandedOtaSteps = (plan: OtaPlanDetails) => {
    const bmFirmwareUpdates = useBmNodeVersionUpdates(plan);

    return useMemo(() => {
        let currentStep = plan.currentStepSequence;
        // Replace known steps with more detail, and expand some steps whose structure we understand into virtual sub-steps (e.g. UpdateBristlemouthFirmware => ['transferring Bristlemouth firmware', ...updatingEachNode])
        // any time we add steps that precede plan.currentStepSequence, we need to advance currentStep by that many steps
        const expandedSteps = ['Initialize OTA update plan'];
        if (plan.status !== 'pending' || currentStep > 0) {
            // skip past the virtual initial step which is just there to make the progress bar look better.
            // leave us at step 0 if we're pending, but if somehow we got into an advanced step on that status, treat
            // it the same as started so we point to the right real step.
            currentStep += 1;
        }
        const updateSpotterFwSteps = filter(plan.steps, { type: 'UpdateSpotterFirmware' });
        expandedSteps.push(
            ...plan.steps.flatMap((step) => {
                // The cases below are exhaustive over the step types we know, so `step` is `never` in the
                // default branch. Read `step.type` here, where it is still a string.
                const stepType = step.type;
                switch (step.type) {
                    case 'UpdateSysConfig':
                        return 'Update Spotter configuration';
                    case 'UpdateHwConfig':
                        return 'Update Spotter hardware configuration';
                    case 'UpdateBmConfiguration':
                        return 'Update Bristlemouth node configuration';
                    case 'UpdateSpotterFirmware': {
                        let fwModifier = '';
                        if (updateSpotterFwSteps.length > 1) {
                            fwModifier = step === last(updateSpotterFwSteps) ? 'final ' : 'intermediate ';
                        }
                        return `Update Spotter to ${fwModifier}firmware ${step.parameters.targetSpotterFirmware}`;
                    }
                    case 'UpdateBristlemouthFirmware': {
                        if (step.sequenceIndex === plan.currentStepSequence) {
                            if (plan.currentState && 'bmNodeId' in plan.currentState) {
                                const currentBmNodeIdx = findIndex(bmFirmwareUpdates, {
                                    bmNodeId: plan.currentState.bmNodeId as string,
                                });
                                currentStep += 1 + currentBmNodeIdx;
                            }
                        } else if (step.sequenceIndex < plan.currentStepSequence) {
                            currentStep += bmFirmwareUpdates.length;
                        }
                        return [
                            'Send new Bristlemouth firmware to Spotter',
                            ...bmFirmwareUpdates.map(({ name }) => `Update ${name}`),
                        ];
                    }
                    default:
                        // just in case there's some new step we don't know about yet
                        return upperFirst(lowerCase(stepType));
                }
            })
        );
        return { currentStep, expandedSteps };
    }, [bmFirmwareUpdates, plan.currentState, plan.currentStepSequence, plan.status, plan.steps]);
};

export const OtaSucceededAlert = ({ plan }: { plan: OtaPlanDetails }) => {
    const { spotterId } = useSpotterContext();
    const { expandedSteps } = useExpandedOtaSteps(plan);
    const totalSteps = expandedSteps.length;
    const { dismissableProps, onDismiss } = useDismissable(`ota-firmware-succeeded:${plan.id}`, {
        spotterId: spotterId!,
    });

    return (
        <Dismissable {...dismissableProps}>
            <Stack sx={{ backgroundColor: cardBgLightGrey, p: 3, position: 'relative' }}>
                <IconButton
                    aria-label="close"
                    onClick={onDismiss}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <GridCloseIcon />
                </IconButton>

                <Typography variant="h5">Firmware update successful</Typography>
                <Typography variant="body2" sx={{ color: minorTextGrey }}>
                    Your system has been successfully updated to all of the latest firmware versions.
                </Typography>
                <StepProgressBar currentStep={totalSteps} totalSteps={totalSteps} stepMessages={expandedSteps} />
                <Typography variant="body2" sx={{ textAlign: 'center' }}>
                    ({totalSteps} of {totalSteps}) Update complete
                </Typography>
            </Stack>
        </Dismissable>
    );
};

export const OtaProgressBox = ({ plan }: { plan: OtaPlanDetails }) => {
    const { expandedSteps, currentStep } = useExpandedOtaSteps(plan);
    const totalSteps = expandedSteps.length;

    return (
        <Stack sx={{ backgroundColor: cardBgLightGrey, p: 3 }}>
            <Typography variant="h5">Firmware update in progress</Typography>
            <Typography variant="body2" sx={{ color: minorTextGrey }}>
                This update can take up to several hours, depending on your system&apos;s cellular connection strength.
                In rare cases, you may experience missing data during the course of this update.
            </Typography>
            <StepProgressBar currentStep={currentStep} totalSteps={totalSteps} stepMessages={expandedSteps} />
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                ({currentStep + 1} of {totalSteps}) {presentParticipleFirst(expandedSteps[currentStep])}...
            </Typography>
        </Stack>
    );
};

export type StepProgressBarProps = { currentStep: number; totalSteps: number; stepMessages?: string[] };

export const StepProgressBar = ({ currentStep, totalSteps, stepMessages }: StepProgressBarProps) => {
    return (
        <Stack direction="row" sx={{ width: 1, alignItems: 'center', justifyContent: 'center', mt: 1.5 }}>
            {range(totalSteps).map((n) => (
                <Fragment key={n}>
                    {n > 0 && (
                        <StepConnector
                            variant={n === currentStep ? 'active' : n < currentStep ? 'complete' : 'queued'}
                        />
                    )}
                    <Tooltip title={stepMessages?.[n]}>
                        <Box
                            sx={{
                                ':first-of-type': { ml: 0 },
                                ':last-of-type': { mr: 0 },
                                mx: '-4px',
                                zIndex: 1,
                                display: 'inline-flex',
                            }}
                        >
                            {n < currentStep ? (
                                <CheckCircleIcon sx={{ color: 'secondary.main' }} />
                            ) : (
                                <CircleIcon
                                    sx={{ color: n === currentStep && n == 0 ? 'secondary.main' : grey[400] }}
                                />
                            )}
                        </Box>
                    </Tooltip>
                </Fragment>
            ))}
        </Stack>
    );
};

const CONNECTOR_HEIGHT_PX = 4;
const STRIPE_WIDTH_PX = 4;
// The gradient repeats every two stripes along its own 45 degree axis. A horizontal distance of
// STRIPE_PERIOD_PX covers that repeat, so tiling and animating at that width keeps the stripes seamless.
const STRIPE_PERIOD_PX = 2 * STRIPE_WIDTH_PX * Math.SQRT2;

/** Draws the bar between the icon for step `followingStep - 1` and the icon for step `followingStep`. */
const StepConnector = ({ variant }: { variant: 'complete' | 'active' | 'queued' }) => {
    const connectorSx = { flex: 1, maxWidth: '160px', height: `${CONNECTOR_HEIGHT_PX}px` };

    if (variant === 'active') {
        return (
            <Box
                sx={{
                    ...connectorSx,
                    backgroundImage: `repeating-linear-gradient(-45deg, ${blue[400]} 0 ${STRIPE_WIDTH_PX}px, ${blue[200]} ${STRIPE_WIDTH_PX}px ${2 * STRIPE_WIDTH_PX}px)`,
                    backgroundSize: `${STRIPE_PERIOD_PX}px 100%`,
                    animation: 'candy-cane-slide 0.8s infinite linear',
                    '@keyframes candy-cane-slide': {
                        '0%': { backgroundPosition: '0 0' },
                        '100%': { backgroundPosition: `${STRIPE_PERIOD_PX}px 0` },
                    },
                }}
            />
        );
    }

    return (
        <Box
            sx={{
                ...connectorSx,
                backgroundColor: variant === 'complete' ? 'secondary.main' : grey[400],
            }}
        />
    );
};
